import * as RadixPopover from '@radix-ui/react-popover';
import {
  type MutableRefObject,
  memo,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
  useCallback,
  useRef,
} from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { PlusIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { usePortalStore } from '@air/primitive-portal';
import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentActionsMenuPanel } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuPanel';
import { COMPOSER_PICKER_BOTTOM_SIDE_OFFSET_PX } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/utils/composerPopoverAnchor';
import { WORKSPACE_AGENT_ACTIONS_MENU_POPOVER_MAX_HEIGHT_PX } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentActionsMenuLayout';
import { workspaceAgentComposerActionsButton } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';
import { useWorkspaceAgentActionsMenu } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentActionsMenu';
import { preserveWorkspaceAgentComposerFocus } from '~/components/v2/components/workspace/workspace-agent-panel/utils/preserveWorkspaceAgentComposerFocus';
import { type Skill } from '~/hooks/queries/skills/usePrivateSkillsList';

const ACTIONS_BUTTON_LABEL = 'Actions';
const ACTIONS_BUTTON_PREFIX = <PlusIcon aria-hidden size={14} />;

/**
 * Radix calls this from a document capture listener. Stopping here ends the dispatch before
 * bubble-phase hotkeys (the board page's multiselect clear) see Escape. No `preventDefault`:
 * the menu still has to close.
 */
const stopActionsMenuEscapeKeyPropagation = (event: globalThis.KeyboardEvent) => {
  event.stopPropagation();
};

const consumeCloseAutoFocusWaiver = (waiverRef: MutableRefObject<boolean>) => {
  const isWaived = waiverRef.current;
  waiverRef.current = false;
  return isWaived;
};

type RestoreActionsMenuSearchFocusParams = {
  isOpen: boolean;
  searchInput: HTMLInputElement | null;
  selectedGroupId: string | null;
};

const restoreActionsMenuSearchFocusIfLost = ({
  isOpen,
  searchInput,
  selectedGroupId,
}: RestoreActionsMenuSearchFocusParams) => {
  if (!isOpen || !searchInput) {
    return;
  }

  if (document.activeElement !== document.body) {
    return;
  }

  searchInput.focus();
  return selectedGroupId;
};

export type PrivateWorkspaceAgentActionsMenuProps = {
  /** The composer box. The menu is positioned against it so the panel matches the chat width. */
  anchorRef: RefObject<HTMLElement>;
  isDisabled: boolean;
  onAddReference: () => void;
  onInsertSkill: (skill: Skill) => void;
};

export const PrivateWorkspaceAgentActionsMenu = memo(
  ({ anchorRef, isDisabled, onAddReference, onInsertSkill }: PrivateWorkspaceAgentActionsMenuProps) => {
    const waiveCloseAutoFocusRef = useRef<boolean>(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const { fillsActionsMenuPopoverHeight, handleOpenChange, isOpen, isSkillsSubview, panelProps, selectedGroup } =
      useWorkspaceAgentActionsMenu({
        isDisabled,
        onAddReference,
        onInsertSkill,
        waiveCloseAutoFocusRef,
      });
    const portal = usePortalStore((state) => state.portal);
    const selectedGroupId = selectedGroup?.id ?? null;

    useIsomorphicLayoutEffect(() => {
      // Keyboard Enter unmounts the focused group/back row; mouse never takes focus from the editor.
      restoreActionsMenuSearchFocusIfLost({
        isOpen,
        searchInput: searchInputRef.current,
        selectedGroupId,
      });
    }, [isOpen, isSkillsSubview, selectedGroupId]);

    const handleTriggerKeyDown = useCallback(
      (event: ReactKeyboardEvent<HTMLButtonElement>) => {
        if (!isDisabled) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
        }
      },
      [isDisabled],
    );

    const handleCloseAutoFocus = useCallback((event: Event) => {
      if (consumeCloseAutoFocusWaiver(waiveCloseAutoFocusRef)) {
        event.preventDefault();
      }
    }, []);

    return (
      <RadixPopover.Root onOpenChange={handleOpenChange} open={isOpen}>
        <RadixPopover.Anchor virtualRef={anchorRef} />
        <RadixPopover.Trigger asChild>
          <Button
            appearance="filled"
            aria-disabled={isDisabled}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            className={workspaceAgentComposerActionsButton()}
            color="gray"
            data-testid="WORKSPACE_AGENT_COMPOSER_ACTIONS_MENU"
            onKeyDown={handleTriggerKeyDown}
            onMouseDown={preserveWorkspaceAgentComposerFocus}
            prefix={ACTIONS_BUTTON_PREFIX}
            size="small"
          >
            {ACTIONS_BUTTON_LABEL}
          </Button>
        </RadixPopover.Trigger>
        <RadixPopover.Portal container={portal}>
          <RadixPopover.Content
            align="start"
            aria-label={ACTIONS_BUTTON_LABEL}
            className="z-portal flex h-auto min-h-0 w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden rounded-12 border ads:border-ads-gray-5 border-gray-5 ads:bg-base bg-gray-1 shadow-popover outline-none dark:bg-gray-3"
            collisionPadding={8}
            data-testid="WORKSPACE_AGENT_ACTIONS_MENU"
            onCloseAutoFocus={handleCloseAutoFocus}
            onEscapeKeyDown={stopActionsMenuEscapeKeyPropagation}
            side="top"
            sideOffset={COMPOSER_PICKER_BOTTOM_SIDE_OFFSET_PX}
            style={
              fillsActionsMenuPopoverHeight
                ? { height: WORKSPACE_AGENT_ACTIONS_MENU_POPOVER_MAX_HEIGHT_PX }
                : { maxHeight: WORKSPACE_AGENT_ACTIONS_MENU_POPOVER_MAX_HEIGHT_PX }
            }
          >
            <div className={cn('flex min-h-0 flex-col', fillsActionsMenuPopoverHeight ? 'flex-1' : 'shrink-0')}>
              <WorkspaceAgentActionsMenuPanel panelProps={panelProps} searchInputRef={searchInputRef} />
            </div>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  },
);

PrivateWorkspaceAgentActionsMenu.displayName = 'PrivateWorkspaceAgentActionsMenu';
